<script>
    import _ from 'lodash'
    import moment from 'moment'
    import { apiComputed, appComputed, authComputed } from '@state/helpers'
    import CONSTANTS from '../constants'
    import Helper from '../utils/helper'
    import $ from '../plugins/vue-gridform/shared/helper'
    import VRatingButton from '../plugins/vue-gridform/components/v-rating-button'
    import CoachingRun from './coaching-run'
    import Coaching from '../router/views/coaching'

    export default {
        components: {
            VRatingButton,
            CoachingRun,
            Coaching
        },

        data() {
            return {
                expanded: [],
                // create/edit dialog
                unitDialog: false,
                editingUnit: null,
                unitForm: {
                    name: '',
                    employeeId: '',
                    startDate: '',
                    endDate: ''
                },
                startDateMenu: false,
                endDateMenu: false,
                saving: false,
                // set-done confirmation dialog
                setDoneDialog: false,
                setDoneTarget: null,
                settingDone: false,
                // delete confirmation dialog
                deleteDialog: false,
                deleteTarget: null,
                deleting: false,
                // add coaching dialog (planning view)
                planningDialog: false,
                planningUnit: null,
                // edit coaching in unit
                coachingRunDialog: false,
                activeCoaching: null,
                coachingMode: 'run'
            }
        },

        computed: {
            ...authComputed,
            ...appComputed,
            ...apiComputed,

            coachingUnits() {
                return this.$store.getters['api/coachingUnits']
            },
            coachings() {
                return this.$store.getters['api/coachings']
            },
            organisationUsersActive() {
                return this.$store.getters['api/organisationUsersActive']
            },
            templates() {
                return this.$store.getters['api/templates']
            },

            tableHeaders() {
                return [
                    { text: '', value: 'data-table-expand', width: '32px', sortable: false },
                    { text: this.$vuetify.lang.t('$vuetify.entity.name'), value: 'name' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.employee'), value: 'employeeName' },
                    { text: this.$vuetify.lang.t('$vuetify.coachingUnit.startDate'), value: 'startDate' },
                    { text: this.$vuetify.lang.t('$vuetify.coachingUnit.endDate'), value: 'endDate' },
                    { text: this.$vuetify.lang.t('$vuetify.coachingUnit.score'), value: 'score', align: 'end', width: '100px' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.state'), value: 'isDone', width: '100px' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.actions'), value: 'actions', sortable: false, align: 'end', width: '120px' }
                ]
            },

            coachingTableHeaders() {
                return [
                    { text: this.$vuetify.lang.t('$vuetify.entity.created'), value: 'created' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.template'), value: 'name' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.rating'), value: 'rating', width: '120px' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.duration'), value: 'duration', align: 'end' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.finished'), value: 'isFinished', width: '120px' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.user'), value: 'username' },
                    { text: this.$vuetify.lang.t('$vuetify.entity.actions'), value: 'actions', sortable: false, align: 'end', width: '80px' }
                ]
            },

            viewTableConfig() {
                return _.defaultsDeep({
                    table: {
                        dense: this.dense,
                        locale: this.language === 'de' ? 'de_DE' : 'en-US'
                    }
                }, CONSTANTS.DEFAULT_TABLE_CONFIG)
            },

            isUnitFormValid() {
                return !!(
                    this.unitForm.name &&
                    this.unitForm.name.trim().length > 0 &&
                    this.unitForm.name.length <= 100 &&
                    this.unitForm.employeeId &&
                    this.unitForm.startDate &&
                    this.unitForm.endDate
                )
            }
        },

        methods: {
            formatDate(date) {
                return date ? moment.utc(date).local().format(this.formatter.date) : ''
            },
            formatDateTime(date) {
                return date ? moment.utc(date).local().format(this.formatter.datetime) : ''
            },
            formatDuration(duration) {
                return duration ? $.formatTimestamp(duration * 1000, false) : ''
            },
            formatScore(score) {
                if (!score && score !== 0) return '—'
                return `${(score * 100).toFixed(1)} %`
            },

            coachingsForUnit(unit) {
                if (!unit || !unit.coachings) return []
                const ids = unit.coachings.map(c => c.id)
                const mapped = this.coachings.filter(c => ids.includes(c.id))
                // include unit coachings that may not yet be in the org coachings store
                const extra = unit.coachings.filter(c => !mapped.find(m => m.id === c.id))
                return [...mapped, ...extra.map(c => ({ ...c, _raw: true }))]
            },

            ratingOptions(item) {
                return {
                    absolute: false,
                    labelPercent: true,
                    rating: item.rating,
                    large: false,
                    toggleHint: false,
                    menu: false
                }
            },
            hasRating(item) {
                return _.get(item, 'rating.max') > 0
            },
            ratingLabel(item) {
                const percent = _.get(item, 'rating.percent')
                if (percent === null || percent === undefined) return ''
                return `${Math.round(percent)} %`
            },

            expandUnit(unit) {
                if (!this.expanded.find(u => u.id === unit.id)) {
                    this.expanded.push(unit)
                }
            },
            collapseUnit(unit) {
                this.expanded = this.expanded.filter(u => u.id !== unit.id)
            },
            isExpanded(unit) {
                return !!this.expanded.find(u => u.id === unit.id)
            },

            // --- Create / Edit Unit ---
            openCreateDialog() {
                this.editingUnit = null
                this.unitForm = { name: '', employeeId: '', startDate: '', endDate: '' }
                this.unitDialog = true
            },
            openEditDialog(unit) {
                this.editingUnit = unit
                this.unitForm = {
                    name: unit.name,
                    employeeId: unit.employeeId,
                    startDate: unit.startDate ? moment.utc(unit.startDate).format('YYYY-MM-DD') : '',
                    endDate: unit.endDate ? moment.utc(unit.endDate).format('YYYY-MM-DD') : ''
                }
                this.unitDialog = true
            },
            closeUnitDialog() {
                this.unitDialog = false
                this.editingUnit = null
            },
            async saveUnit() {
                if (!this.isUnitFormValid) return
                this.saving = true
                try {
                    const organisationId = this.organisation.organisationId
                    const startDate = this.unitForm.startDate ? moment(this.unitForm.startDate).toISOString() : null
                    const endDate = this.unitForm.endDate ? moment(this.unitForm.endDate).toISOString() : null

                    if (this.editingUnit) {
                        await this.$store.dispatch('api/COACHING_UNIT_UPDATE', {
                            coachingUnitId: this.editingUnit.id,
                            name: this.unitForm.name,
                            startDate,
                            endDate
                        })
                    } else {
                        await this.$store.dispatch('api/COACHING_UNIT_CREATE', {
                            name: this.unitForm.name,
                            employeeId: this.unitForm.employeeId,
                            organisationId,
                            startDate,
                            endDate
                        })
                    }
                    this.$toast(this.$vuetify.lang.t('$vuetify.coachingUnit.saved'), { type: CONSTANTS.TYPE_SUCCESS })
                    this.closeUnitDialog()
                } catch (e) {
                    // error handled by store
                } finally {
                    this.saving = false
                }
            },

            // --- Set Done ---
            openSetDoneDialog(unit) {
                this.setDoneTarget = unit
                this.setDoneDialog = true
            },
            async confirmSetDone() {
                if (!this.setDoneTarget) return
                this.settingDone = true
                try {
                    await this.$store.dispatch('api/COACHING_UNIT_SET_DONE', this.setDoneTarget.id)
                    this.setDoneDialog = false
                    this.setDoneTarget = null
                } catch (e) {
                    // error handled by store
                } finally {
                    this.settingDone = false
                }
            },

            // --- Delete ---
            openDeleteDialog(unit) {
                this.deleteTarget = unit
                this.deleteDialog = true
            },
            async confirmDelete() {
                if (!this.deleteTarget) return
                this.deleting = true
                try {
                    await this.$store.dispatch('api/COACHING_UNIT_DELETE', this.deleteTarget.id)
                    this.deleteDialog = false
                    this.deleteTarget = null
                } catch (e) {
                    // error handled by store
                } finally {
                    this.deleting = false
                }
            },

            // --- Add / Edit coaching in unit ---
            openPlanningForUnit(unit) {
                this.planningUnit = unit
                this.planningDialog = true
            },
            closePlanning() {
                this.planningDialog = false
                this.planningUnit = null
            },
            onPlanningEdit(coaching) {
                this.closePlanning()
                this.editCoachingInUnit(coaching)
            },
            editCoachingInUnit(coaching) {
                const full = this.coachings.find(c => c.id === coaching.id) || coaching
                this.activeCoaching = full
                this.coachingMode = 'run'
                this.coachingRunDialog = true
            },
            closeCoachingRun() {
                this.coachingRunDialog = false
                this.activeCoaching = null
            }
        }
    }
</script>

<template>
    <VLayout class="justify-center position-relative">
        <VFlex xs12 xl10 class="pa-2 px-md-8">
            <VDataTable
                v-bind="viewTableConfig.table"
                :headers="tableHeaders"
                :items="coachingUnits"
                :expanded.sync="expanded"
                item-key="id"
                show-expand
                :dense="dense"
                class="elevation-4">

                <!-- Toolbar -->
                <template #top>
                    <VToolbar v-bind="viewTableConfig.toolbar" :dense="dense">
                        <VToolbarTitle :class="`font-weight-bold ${theme === 'dark' ? 'black--text' : 'white--text'}`">
                            {{ $vuetify.lang.t('$vuetify.coachingUnit.label') }}
                        </VToolbarTitle>
                        <VSpacer/>
                        <VBtn v-if="canEdit" icon :light="theme === 'dark'" :dark="theme !== 'dark'" @click.left="openCreateDialog">
                            <VIcon>mdi-plus</VIcon>
                        </VBtn>
                    </VToolbar>
                </template>

                <!-- Row template -->
                <template v-slot:item="{ item, isExpanded }">
                    <tr>
                        <td class="pl-2 pr-1">
                            <VIcon @click.left="isExpanded ? collapseUnit(item) : expandUnit(item)">
                                {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </VIcon>
                        </td>
                        <td class="font-weight-medium">{{ item.name }}</td>
                        <td>{{ item.employeeName }}</td>
                        <td class="vc-monospace text-no-wrap">{{ formatDate(item.startDate) }}</td>
                        <td class="vc-monospace text-no-wrap">{{ formatDate(item.endDate) }}</td>
                        <td class="text-right vc-monospace">{{ formatScore(item.score) }}</td>
                        <td>
                            <VChip v-if="item.isDone" x-small color="grey" dark>
                                <VIcon x-small left>mdi-lock</VIcon>
                                {{ $vuetify.lang.t('$vuetify.coachingUnit.isDone') }}
                            </VChip>
                            <VChip v-else x-small color="primary" dark>
                                {{ $vuetify.lang.t('$vuetify.coachingUnit.isOpen') }}
                            </VChip>
                        </td>
                        <td class="text-right">
                            <div class="text-no-wrap">
                                <VBtn v-if="canEdit && !item.isDone" icon small @click.left="openEditDialog(item)">
                                    <VIcon small>mdi-pencil</VIcon>
                                </VBtn>
                                <VBtn v-if="canEdit && !item.isDone" icon small :title="$vuetify.lang.t('$vuetify.coachingUnit.setDone')" @click.left="openSetDoneDialog(item)">
                                    <VIcon small>mdi-lock-outline</VIcon>
                                </VBtn>
                                <VBtn v-if="canDelete" icon small @click.left="openDeleteDialog(item)">
                                    <VIcon small>mdi-delete</VIcon>
                                </VBtn>
                            </div>
                        </td>
                    </tr>
                </template>

                <!-- Expanded row: show coachings inside this unit -->
                <template v-slot:expanded-item="{ item, headers }">
                    <tr>
                        <td :colspan="headers.length" class="pa-0">
                            <VSheet class="ml-8 mr-2 my-2" outlined rounded>
                                <VDataTable
                                    :headers="coachingTableHeaders"
                                    :items="coachingsForUnit(item)"
                                    :dense="dense"
                                    hide-default-footer
                                    :items-per-page="-1"
                                    item-key="id"
                                    class="elevation-0">

                                    <template #top>
                                        <VToolbar flat dense>
                                            <VToolbarTitle class="subtitle-2">
                                                {{ item.name }} — {{ coachingsForUnit(item).length }} {{ $vuetify.lang.t('$vuetify.routes.coaching').toLowerCase() }}
                                            </VToolbarTitle>
                                            <VSpacer/>
                                            <VBtn v-if="canEdit && !item.isDone" small text @click.left="openPlanningForUnit(item)">
                                                <VIcon left small>mdi-plus</VIcon>
                                                {{ $vuetify.lang.t('$vuetify.coachingUnit.addCoaching') }}
                                            </VBtn>
                                        </VToolbar>
                                    </template>

                                    <template v-slot:item="{ item: coaching }">
                                        <tr>
                                            <td><span class="vc-monospace text-no-wrap">{{ formatDateTime(coaching.created) }}</span></td>
                                            <td>{{ coaching.name }}</td>
                                            <td>
                                                <div v-if="hasRating(coaching)" class="text-no-wrap">
                                                    <VRatingButton :options="ratingOptions(coaching)"/>
                                                    <span class="vc-monospace d-inline-block">{{ ratingLabel(coaching) }}</span>
                                                </div>
                                                <span v-else class="grey--text caption">{{ $vuetify.lang.t('$vuetify.coaching.noRatingHintShort') }}</span>
                                            </td>
                                            <td class="text-right vc-monospace">{{ formatDuration(coaching.duration) }}</td>
                                            <td>
                                                <VCheckbox :input-value="coaching.isFinished" class="mt-0 pt-0 d-inline-block" dense hide-details disabled :ripple="false"/>
                                            </td>
                                            <td>{{ coaching.username }}</td>
                                            <td class="text-right">
                                                <VBtn v-if="canEdit && !item.isDone" icon small @click.left="editCoachingInUnit(coaching)">
                                                    <VIcon small>mdi-pencil</VIcon>
                                                </VBtn>
                                            </td>
                                        </tr>
                                    </template>

                                    <template #no-data>
                                        <span class="grey--text caption">{{ $vuetify.lang.t('$vuetify.coachingUnit.noCoachings') }}</span>
                                    </template>
                                </VDataTable>
                            </VSheet>
                        </td>
                    </tr>
                </template>

                <template #no-data>
                    <span class="grey--text">{{ $vuetify.lang.t('$vuetify.coachingUnit.label') }}: 0</span>
                </template>
            </VDataTable>
        </VFlex>

        <!-- Create / Edit Unit Dialog -->
        <VDialog v-model="unitDialog" max-width="560px" persistent>
            <VCard>
                <VCardTitle class="primary white--text">
                    {{ editingUnit
                        ? $vuetify.lang.t('$vuetify.coachingUnit.edit')
                        : $vuetify.lang.t('$vuetify.coachingUnit.create') }}
                </VCardTitle>
                <VCardText class="pt-4">
                    <VTextField
                        v-model="unitForm.name"
                        :label="$vuetify.lang.t('$vuetify.coachingUnit.name')"
                        :counter="100"
                        maxlength="100"
                        :dense="dense"
                        outlined
                        required
                    />
                    <VSelect
                        v-model="unitForm.employeeId"
                        :label="$vuetify.lang.t('$vuetify.coachingUnit.selectEmployee')"
                        :items="organisationUsersActive"
                        item-text="username"
                        item-value="userId"
                        :dense="dense"
                        outlined
                        :disabled="!!editingUnit"
                        required
                    />
                    <VRow>
                        <VCol cols="6">
                            <VMenu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <VTextField
                                        v-model="unitForm.startDate"
                                        :label="$vuetify.lang.t('$vuetify.coachingUnit.startDate')"
                                        :dense="dense"
                                        outlined
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <VDatePicker v-model="unitForm.startDate" no-title @input="startDateMenu = false"/>
                            </VMenu>
                        </VCol>
                        <VCol cols="6">
                            <VMenu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <VTextField
                                        v-model="unitForm.endDate"
                                        :label="$vuetify.lang.t('$vuetify.coachingUnit.endDate')"
                                        :dense="dense"
                                        outlined
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    />
                                </template>
                                <VDatePicker v-model="unitForm.endDate" no-title @input="endDateMenu = false"/>
                            </VMenu>
                        </VCol>
                    </VRow>
                </VCardText>
                <VCardActions>
                    <VSpacer/>
                    <VBtn text @click.left="closeUnitDialog">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn color="primary" :loading="saving" :disabled="!isUnitFormValid || saving" @click.left="saveUnit">
                        {{ $vuetify.lang.t('$vuetify.actions.save') }}
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>

        <!-- Set Done Confirmation Dialog -->
        <VDialog v-model="setDoneDialog" max-width="440px" persistent>
            <VCard>
                <VCardTitle class="warning white--text">
                    {{ $vuetify.lang.t('$vuetify.coachingUnit.setDone') }}
                </VCardTitle>
                <VCardText class="pt-4">
                    <p>{{ $vuetify.lang.t('$vuetify.coachingUnit.setDoneHint') }}</p>
                    <p v-if="setDoneTarget" class="font-weight-bold">{{ setDoneTarget.name }}</p>
                </VCardText>
                <VCardActions>
                    <VSpacer/>
                    <VBtn text @click.left="setDoneDialog = false">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn color="warning" dark :loading="settingDone" :disabled="settingDone" @click.left="confirmSetDone">
                        {{ $vuetify.lang.t('$vuetify.coachingUnit.setDoneSubmit') }}
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>

        <!-- Delete Confirmation Dialog -->
        <VDialog v-model="deleteDialog" max-width="440px" persistent>
            <VCard>
                <VCardTitle class="error white--text">
                    {{ $vuetify.lang.t('$vuetify.coachingUnit.delete') }}
                </VCardTitle>
                <VCardText class="pt-4">
                    <p v-if="deleteTarget" class="font-weight-bold">{{ deleteTarget.name }}</p>
                </VCardText>
                <VCardActions>
                    <VSpacer/>
                    <VBtn text @click.left="deleteDialog = false">{{ $vuetify.lang.t('$vuetify.actions.cancel') }}</VBtn>
                    <VBtn color="error" :loading="deleting" :disabled="deleting" @click.left="confirmDelete">
                        {{ $vuetify.lang.t('$vuetify.actions.delete') }}
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>

        <!-- Add Coaching to Unit (Planning Dialog) -->
        <VDialog v-if="canEdit" v-model="planningDialog" persistent no-click-animation fullscreen>
            <Coaching v-model="planningDialog" :coaching-unit-id="planningUnit ? planningUnit.id : null" @edit="onPlanningEdit" @close="closePlanning"/>
        </VDialog>

        <!-- Edit Coaching in Unit -->
        <VDialog v-model="coachingRunDialog" persistent no-click-animation fullscreen>
            <CoachingRun v-model="coachingRunDialog" :coaching="activeCoaching" :mode="coachingMode" @close="closeCoachingRun"/>
        </VDialog>
    </VLayout>
</template>
